import { GroupType, ZGroup } from "src/types/ZGroup";
import { ZUid } from "src/types/zUid";
import { loadTranslation } from "src/common/api/apiTranslation";
import { getGroupTypeIcon } from "../../Obj2Tab/utils/itemIcons";
import { deleteGroup } from "../../objectsApi";
import { loadDictionaryValues } from "../api/objects";
import {
  GroupNode,
  TreeNode,
  groupNodeId,
  unsavedId,
  valueNode,
} from "../model/node";
import { allowedActions } from "../model/permissions";
import { group2ed } from "../../Obj2Tab/EdGroup";
import { saveGroupToOwner } from "../../Obj2Tab/utils/groupAndAttrSave";
import { childNodesOf } from "./childNodes";
import { renderNodeTitle } from "./title";
import { saveOwnerOf } from "./saveOwner";
import {
  KindContext,
  LazyKindHandler,
  RemovableKindHandler,
  SavableKindHandler,
  requireDetails,
} from "./types";

const findGroup = (
  groups: ZGroup[] | null | undefined,
  groupId: ZUid,
): ZGroup | undefined => {
  // eslint-disable-next-line no-restricted-syntax
  for (const group of groups ?? []) {
    if (group.id === groupId) return group;
    const nested = findGroup(group.groups, groupId);
    if (nested) return nested;
  }
  return undefined;
};

type GroupStrategy = {
  canExpand(node: GroupNode): boolean;
  load(node: GroupNode, ctx: KindContext): Promise<TreeNode[]>;
};

/**
 * Поведение группы по её типу — таблица по дискриминанту `groupType.name`,
 * а не ветвление внутри фабрики узлов, как было в v1. Всё, что отличает один
 * тип группы от другого, лежит в одной записи.
 *
 * Отдельным модулем не вынесена: обе записи короче четырёх строк, и разносить
 * их по файлам значило бы платить за структуру больше, чем она даёт.
 */
const strategyByGroupType: Record<GroupType, GroupStrategy> = {
  // Мнемоническая группа уже пришла внутри деталей объекта: сеть не нужна
  [GroupType.Mnemonic]: {
    canExpand: () => true,
    load: async (node, ctx) => {
      const owner = ctx.ownerObject(node.id);
      if (!owner) throw Error(`Не найден объект для группы ${node.data.name}`);
      const details = requireDetails(ctx, owner.data.id);
      // Группы в деталях может не оказаться, если структура успела измениться:
      // это не ошибка программиста, детей просто нет
      const group = findGroup(details.groups, node.data.id);
      return childNodesOf(node.id, group?.attributes, group?.groups, {
        includeValueGroups: true,
      });
    },
  },

  // Значения справочника грузятся только по раскрытию — это и есть та
  // ленивость, которую в v1 приходилось беречь вручную при каждой правке
  [GroupType.ByDictionary]: {
    canExpand: (node) => !!node.data.attributeId,
    load: async (node) => {
      const { attributeId } = node.data;
      if (!attributeId) return [];
      const values = await loadDictionaryValues(attributeId);
      return values.map((value) => valueNode(value, node.id));
    },
  },
};

const strategyOf = (node: GroupNode): GroupStrategy =>
  strategyByGroupType[node.data.groupType.name];

export const groupKind: LazyKindHandler<GroupNode> &
  RemovableKindHandler<GroupNode> &
  SavableKindHandler<GroupNode> = {
  title: (node, actions) =>
    renderNodeTitle({
      name: node.data.name,
      id: node.data.id,
      displayId: node.data.displayId,
      disabled: !actions.update,
      placeholder: "Новая группа",
    }),

  icon: (node) => getGroupTypeIcon(node.data.groupType.name),

  // Своих прав у группы нет: она наследует права объекта целиком
  ownActions: () => allowedActions,

  isExpandable: (node) => strategyOf(node).canExpand(node),

  children: (node, ctx) => strategyOf(node).load(node, ctx),

  formData: async (node) => {
    const { translations } = await loadTranslation(node.data.name);
    return group2ed({ group: node.data, translations });
  },

  removeMessage: () => "Удалить группу атрибутов?",

  remove: async (node) => {
    if (node.data.id !== unsavedId) await deleteGroup(node.data.id);
  },

  save: async (node, values, ctx) => {
    const saved = await saveGroupToOwner(
      saveOwnerOf(node, ctx),
      node.data,
      values,
    );
    const owner = ctx.ownerObject(node.id);
    if (!owner) throw Error(`Не найден объект для группы ${node.data.name}`);
    // Структура объекта изменилась: и сама группа, и её место в дереве
    return { objectId: owner.data.id, selectId: groupNodeId(saved.id) };
  },
};
